import {_} from 'lodash';
import * as ko from "knockout";
import {ObjectTypeId, TEXT_WRAPPER_END, TEXT_WRAPPER_START} from "../../constants";
import {global} from "../../appContext";
import pubsub from "../../pubsub";
import {viewModel as ListViewModel} from "./list";
import {viewModel as HighlightViewModel} from "./highlight";
import {ISyncField, TBaseOrObservableType} from "../../index";
import {IAbstractInputParams, IBoundsAndRange, ITextRange} from "./index";
import {KOSubscriptionTracker} from "../../util/KOSubscriptionTracker";
import {getNextId, isEnabledComputedFactory} from "../../util/misc";

export default abstract class AbstractText<TElement extends HTMLElement = HTMLElement> {
    protected static readonly ElementIdPrefix = 'dynamic-variable-input-';
    private readonly isEnabled: KnockoutComputed<boolean>;
    protected readonly kosub: KOSubscriptionTracker = new KOSubscriptionTracker();
    protected highlight!: HighlightViewModel;
    protected isOperational: boolean = true;
    protected list!: ListViewModel;
    protected readonly attr: any;
    protected readonly expression: KnockoutObservable<string>;
    protected onEventHandler: any;
    protected onKeyHandler: any;
    protected readonly rows: number;
    protected srcEl!: TElement;
    public readonly dynamicSupport: boolean;
    public readonly id: string;
    protected shadow: HTMLDivElement = document.createElement('div');

    // these are the styles we'll copy to the shadow element to get accurate calculation of bounds
    protected static readonly ShadowStyleProperies: string[] = [
        'border',
        'box-sizing',
        'font',
        'font-family',
        'font-kerning',
        'font-size',
        'height',
        'letter-spacing',
        'line-height',
        'margin',
        'max-height',
        'max-width',
        'min-height',
        'min-width',
        'padding',
        'text-align',
        'text-indent',
        'text-rendering',
        'text-transform',
        'white-space',
        'width',
        'word-spacing',
        'writing-mode',
    ];
    protected resizeObserver!: ResizeObserver;
    private readonly event: any;

    constructor(protected params: IAbstractInputParams) {
        this.id = AbstractText.ElementIdPrefix + getNextId();
        this.rows = params.rows || 10;
        this.expression = params.expression;
        this.dynamicSupport = params.dynamicSupport ?? true;
        this.event = params.event ?? {}
        this.attr = _.defaults(params.attr ?? {}, {
            id: this.id,
            placeholder: params.placeholder ?? '',
            type: params.type ?? 'text',
            dynamicsupport: this.dynamicSupport,
            rows: this.rows,
        });
        this.isEnabled = isEnabledComputedFactory(params);

        pubsub.on(this, 'viewModel.initialized', (e: JQuery.Event, params: { id: string, vm: any }) => {
            if (params.id !== this.id) {
                return;
            }
            switch (params.vm.constructor) {
                case ListViewModel:
                    this.list = params.vm;
                    this.list.init(this.objectTypeId, this.objectTypeIds, this.onListItemSelected.bind(this), this.insertParameter?.bind(this) ?? null, this.params.sort);
                    break;
                case HighlightViewModel:
                    this.highlight = params.vm;
                    break;
            }
        });

        this.kosub.subscribe(this.objectTypeId, () => {
            this.list?.initObjectTypeIds(this.objectTypeId, this.objectTypeIds);
        });
        this.kosub.subscribe(this.objectTypeIds, () => {
            this.list?.initObjectTypeIds(this.objectTypeId, this.objectTypeIds);
        });

        global.listManager.add(this.id);
        global.highlightManager.add(this.id);

        requestAnimationFrame(() => {
            this.init()
        })

    }

    protected init() {
        // now we can query the element
        this.srcEl = this.getEl();

        this.onEventHandler = this.onEvent.bind(this);
        this.onKeyHandler = this.onKey.bind(this);

        if (this.srcEl) {
            this.resizeObserver = new ResizeObserver(() => this.onResize());
            this.resizeObserver.observe(this.srcEl);
        }

        this.onViewReady()
    }

    protected onViewReady(): void {
        this.updateShadowStyle()

        // inherited class should expand on this method...
    }

    protected onResize() {
        this.updateShadowStyle()
    }

    protected updateShadowStyle() {
        AbstractText.ShadowStyleProperies.forEach((prop: string) => {
            try {
                let val = document.defaultView?.getComputedStyle(this.srcEl).getPropertyValue(prop);
                if (val != null) {
                    this.shadow.style.setProperty(prop, val);
                }
            } catch (err) {
            }
        });

        // this.el.style.display = "none";
        this.shadow.style.position = "absolute";
        this.shadow.style.top = "0";
        this.shadow.style.left = "0";
        this.shadow.style.userSelect = "none";
        this.shadow.style.pointerEvents = "none";
        this.shadow.style.visibility = "hidden";
        this.shadow.style.overflow = "auto";
        this.shadow.style.wordBreak = "break-word";

        // debug: see what's going on!
        // this.shadow.style.outline = "2px solid red";
        // this.shadow.style.outlineOffset = '-1px';
        // this.shadow.style.visibility = "visible";
        // this.shadow.style.opacity = ".5";
        // this.shadow.style.zIndex = '99999';
        // this.shadow.style.color = 'red';
    }

    protected getEl(): TElement {
        return document.querySelector('#' + this.id) as TElement;
    }

    public abstract addEventListener(event: string, handler: () => void): void;

    public abstract removeEventListener(event: string, handler: () => void): void;

    protected abstract setCaretPosition(position: number): void;

    protected abstract getDocument(): Document;

    protected onKey(e: KeyboardEvent): boolean {
        const obj = this.getBoundsAndRange();
        switch (e.key) {
            case 'ArrowUp':
            case 'ArrowDown':
                if (obj && this.isOperational && this.list.getNumberOfItems()) {
                    if (e.type == 'keydown' && this.list.getNumberOfItems() > 1) {
                        switch (e.key) {
                            case 'ArrowUp':
                                this.list.indexDec();
                                break;
                            case 'ArrowDown':
                                this.list.indexInc();
                                break;
                        }
                    }
                    e.preventDefault();
                    return false;
                }
                break;
            case 'Enter':
                if (e.type == 'keydown' && obj && this.isOperational && this.list.getNumberOfItems()) {
                    const listSyncfield = this.list.getSyncfield();
                    if (!listSyncfield || !this.canInsertVariableFromList(listSyncfield)) {
                        return true;
                    }
                    e.preventDefault();
                    this.insertVariable(listSyncfield);
                    return false;
                }
                break;
            case 'Escape':
                if (obj) {
                    if (e.type == 'keydown') {
                        this.isOperational = !this.isOperational;
                        e.preventDefault();
                    }
                }
                break;
            case 'v':
                if (e.type == 'keydown') {
                    if (obj && this.isOperational && e.altKey && this.list.isParameterDialogable()) {
                        e.preventDefault();
                        this.list.initParameterDialog();
                    }
                }
                break;
        }
        this.onEvent();
        return true;
    }

    protected onEvent(e: Event | null = null) {
        if (e?.type == 'focusin') {
            global.textEditor(this);
        }

        window.requestAnimationFrame(() => {
            const obj = this.getBoundsAndRange();
            this.updateParamsExpression();

            const noObject = !obj;
            const notFocused = !this.hasFocus();
            const notOperational = !this.isOperational;

            if (noObject || notFocused || notOperational) {
                this.highlight?.rect(null);
                this.list?.show(null);
                if ((noObject || notFocused) && notOperational) {
                    this.isOperational = true;
                }
                return true;
            }

            this.showList(obj.textRange, obj.textBounds);
        })
    }

    protected abstract updateParamsExpression(): void;

    protected abstract hasFocus(): boolean;

    protected abstract showList(textRange: ITextRange, textBounds: ClientRect): void;

    protected getVariableNameObject(syncfield: ISyncField): { raw: string, displayname: string, isFunction: boolean, delimiterIndexes: number[] } {
        let raw: string;
        let displayname: string;
        const isFunction = [ObjectTypeId.Function, ObjectTypeId.FunctionAlias].includes(syncfield.objecttypeid);
        switch (syncfield.objecttypeid) {
            case ObjectTypeId.FunctionAlias:
                raw = syncfield.displayname ?? syncfield.name;
                displayname = this.stripTextWrappers(syncfield.displayname ?? 'N/A');
                break;
            default:
                raw = syncfield.name;
                displayname = this.stripTextWrappers(raw);
        }

        const regex = /;/g;
        let exec: RegExpExecArray | null;
        let delimiterIndexes: number[] = [];

        while ((exec = regex.exec(displayname)) !== null) {
            delimiterIndexes.push(regex.lastIndex);
        }

        return {raw, displayname, isFunction, delimiterIndexes};
    }

    private stripTextWrappers(str: string): string {
        const a = str.charAt(0) == TEXT_WRAPPER_START;
        const b = str.charAt(str.length - 1) == TEXT_WRAPPER_END;
        if (a && b) {
            return str.substring(1, str.length - 1);
        }
        return str;
    }

    /** False when a function template is already partially filled (semicolon typed inside brackets). */
    protected canInsertVariableFromList(syncfield: ISyncField): boolean {
        const obj = this.getBoundsAndRange();
        if (!obj) {
            return false;
        }
        return !(syncfield.name.includes(';') && obj.textRange.str.includes(';'));
    }

    private onListItemSelected(syncfield: ISyncField) {
        if (this.canInsertVariableFromList(syncfield)) {
            this.insertVariable(syncfield);
        }
    }

    protected abstract insertVariable(syncfield: ISyncField): void;

    protected abstract insertParameter(syncfield: ISyncField, value: string, index: number): void;

    public abstract insertString(str: string): void;

    protected abstract getBoundsAndRange(): IBoundsAndRange | null;

    public abstract getTextRange(): ITextRange | null;

    public objectTypeId = ko.pureComputed((): ObjectTypeId => {
        return ko.unwrap(this.params.objectTypeId as TBaseOrObservableType<ObjectTypeId>);
    })

    public objectTypeIds: KnockoutComputed<ObjectTypeId[]> = ko.pureComputed((): ObjectTypeId[] => {
        return (ko.unwrap(this.params.objectTypeIds) ?? []).map(id => ko.unwrap(id));
    })

    dispose() {
        pubsub.off(this);
        this.resizeObserver?.unobserve(this.srcEl);
        this.resizeObserver?.disconnect()
        this.kosub.unsuscribeAll();
        global.highlightManager.remove(this.id);
        global.listManager.remove(this.id);
        this.isEnabled.dispose();
    }

}
